import {
  useBilling,
  useEnabledFeatures,
  useHasEnabledFeatures,
  useTeam,
  useTeamRole,
} from '@vrplatform/data';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import { Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { VerifyTokenView } from '../../views/invite-redirect/VerifyTokenView';
import { lazyWithRetry } from './_utils';

const PmDashboard = lazyWithRetry(() => import('../../views/dashboard/ledger'));
const LegacyPmDashboard = lazyWithRetry(
  () => import('../../views/dashboard/legacy')
);

const EditAutomationView = lazyWithRetry(
  () => import('../../views/automations/views/edit-automation')
);

const AutomationsOverviewView = lazyWithRetry(
  () => import('../../views/automations/views/overview-automations')
);
const FeaturesView = lazyWithRetry(() => import('../../views/features'));

const ConnectionListView = lazyWithRetry(
  () => import('../../views/connections/list')
);

const ConnectionDetailView = lazyWithRetry(
  () => import('../../views/connections/detail/ConnectionDetailView')
);

const ConnectionWorkflowView = lazyWithRetry(
  () => import('../../views/connections/detail/ConnectionWorkflowView')
);

const LegacyStatementListView = lazyWithRetry(
  () => import('../../views/statements/legacy/statement-list/StatementListView')
);
const StatementListView = lazyWithRetry(
  () => import('../../views/statements/statement-list/StatementListView')
);
const LegacyTemplateView = lazyWithRetry(
  () => import('../../views/statements/legacy/template-settings')
);
const LegacyStatementView = lazyWithRetry(
  () => import('../../views/statements/legacy/statement')
);
const PmStatementView = lazyWithRetry(
  () => import('../../views/statements/pm-statement')
);
const StatementView = lazyWithRetry(
  () => import('../../views/statements/statement')
);
const TaxStatementSettingsView = lazyWithRetry(
  () => import('../../views/statements/legacy/1099')
);
const LegacySummaryView = lazyWithRetry(
  () => import('../../views/statements/legacy/summary')
);
const LegacyReservationsView = lazyWithRetry(
  () => import('../../views/reservations/legacy/ReservationsView')
);
const ReservationsView = lazyWithRetry(
  () => import('../../views/reservations/ledger/ReservationsView')
);

const GlobalMappingsView = lazyWithRetry(
  () => import('../../views/global-mappings')
);

const FilesView = lazyWithRetry(() => import('../../views/files'));

const ListingListView = lazyWithRetry(
  () => import('../../views/listings/list/ledger/ListingsView')
);

const LegacyListingListView = lazyWithRetry(
  () => import('../../views/listings/list/legacy/ListingsView')
);

const ListingDetailView = lazyWithRetry(
  () => import('../../views/listings/detail/ListingDetailView')
);

const LegacyOwnersView = lazyWithRetry(
  () => import('../../views/owners/legacy')
);

const OwnersView = lazyWithRetry(() => import('../../views/owners/ledger'));

const OwnerDetailView = lazyWithRetry(
  () => import('../../views/owners/ledger/detail/OwnerDetailView')
);

const GettingStartedView = lazyWithRetry(
  () => import('../../views/getting-started/GettingStartedView')
);
const PaymentsViewV2 = lazyWithRetry(() => import('../../views/payments'));

const SettingsView = lazyWithRetry(() => import('../../views/settings'));

const ReportsView = lazyWithRetry(
  () => import('../../views/reports/ReportsView')
);

const TestingView = lazyWithRetry(
  () => import('../../views/testing/TestingView')
);

const TrpcTestingView = lazyWithRetry(
  () => import('../../views/testing/TrpcTestingView')
);

const EmbedTestingView = lazyWithRetry(
  () => import('../../views/testing/EmbedTestingView')
);

const StatementLayoutsView = lazyWithRetry(
  () => import('../../views/statements/layouts')
);

const CustomFeesView = lazyWithRetry(
  () => import('../../views/custom-fees/CustomFeesView')
);

const FeeRoutes = lazyWithRetry(() => import('../../views/fees/FeesRoutes'));

const FeeEditView = lazyWithRetry(
  () => import('../../views/fees/edit/FeeEditView')
);

const GeneralLedgerView = lazyWithRetry(
  () => import('../../views/general-ledger/GeneralLedgerView')
);

const ExpensesOverviewView = lazyWithRetry(
  () => import('../../views/expenses/list')
);
const ExpenseEditView = lazyWithRetry(
  () => import('../../views/expenses/edit')
);
const ExpenseDetailView = lazyWithRetry(
  () => import('../../views/expenses/detail')
);
const RecurringExpenseDetailView = lazyWithRetry(
  () => import('../../views/expenses/recurring/detail')
);
const ReconciliationView = lazyWithRetry(
  () => import('../../views/reconciliation')
);
const PlaidCallbackView = lazyWithRetry(
  () => import('../../views/reconciliation/plaid-callback')
);
const DepositsOverviewView = lazyWithRetry(
  () => import('../../views/deposits/list')
);
const DepositDetailView = lazyWithRetry(
  () => import('../../views/deposits/detail')
);
const DepositEditView = lazyWithRetry(
  () => import('../../views/deposits/edit')
);

export const PmRoutes = () => {
  const { search } = useLocation();

  const { SETUP_GUIDE_UI } = useEnabledFeatures();
  const [{ isGL: GL }] = useTeam();

  const hasEnabledFeatures = useHasEnabledFeatures();

  const { isReadOnly } = useBilling();
  const { isVrpAdmin } = useTeamRole();

  return (
    <Suspense fallback={<LoadingIndicator isFullPageLoading />}>
      <Routes>
        <Route
          path="token-verify"
          element={<VerifyTokenView isAuthenticated />}
        />

        <Route index element={GL ? <PmDashboard /> : <LegacyPmDashboard />} />

        <Route path="dashboard">
          <Route
            index
            element={<Navigate to="/dashboard/overview" replace />}
          />
          <Route
            path=":tabValue"
            element={GL ? <PmDashboard /> : <LegacyPmDashboard />}
          />
        </Route>

        {SETUP_GUIDE_UI && (
          <Route path="getting-started" element={<GettingStartedView />} />
        )}

        <Route
          path="reservations"
          element={GL ? <ReservationsView /> : <LegacyReservationsView />}
        />
        <Route path="payments" element={<PaymentsViewV2 />} />
        <Route path="mappings" element={<GlobalMappingsView />}>
          <Route index element={<GlobalMappingsView />} />
          <Route path=":tabValue" element={<GlobalMappingsView />} />
        </Route>

        {GL ? (
          <>
            <Route path="listings" element={<ListingListView />} />
            <Route path="listings/:tabValue" element={<ListingListView />} />
            <Route path="listing/:listingId" element={<ListingDetailView />} />
            <Route
              path="listing/:listingId/:tabValue/*"
              element={<ListingDetailView />}
            />
          </>
        ) : (
          <Route path="listings" element={<LegacyListingListView />} />
        )}

        {GL ? (
          <>
            <Route path="owners" element={<OwnersView />} />
            <Route path="owners/:tabValue" element={<OwnersView />} />
            <Route path="owner/:contactId" element={<OwnerDetailView />} />
            <Route
              path="owner/:contactId/:tabValue/*"
              element={<OwnerDetailView />}
            />
          </>
        ) : (
          <Route path="owners/*" element={<LegacyOwnersView />} />
        )}

        {GL ? (
          <>
            <Route path="connections/*" element={<ConnectionListView />} />
            <Route
              path="connection/:connectionId"
              element={<ConnectionDetailView />}
            />
            <Route
              path="connection/:connectionId/workflows/:flowId"
              element={<ConnectionWorkflowView />}
            />
            <Route
              path="connection/:connectionId/:tabValue/*"
              element={<ConnectionDetailView />}
            />
          </>
        ) : (
          <Route path="connections/*" element={<ConnectionListView />} />
        )}

        <Route path="files" index element={<FilesView />} />

        <Route path="testing" element={<TestingView />} />
        <Route path="testing2" element={<TrpcTestingView />} />
        {GL && (
          <Route
            path="embed"
            element={
              isVrpAdmin ? <EmbedTestingView /> : <Navigate to="/" replace />
            }
          />
        )}

        <Route path="automations" element={<AutomationsOverviewView />} />
        <Route path="automations/:id" element={<EditAutomationView />} />

        <Route
          path="owner-statements"
          element={<Navigate to={`/statements${search}`} replace />}
        />

        {GL && (
          <Route
            path="pm-statement/:month/:tabValue"
            element={<PmStatementView />}
          />
        )}

        <Route path="statement">
          {GL ? (
            <Route path=":periodId" element={<StatementView />} />
          ) : (
            <Route path=":listingId" element={<LegacyStatementView />} />
          )}
        </Route>

        <Route path="statements">
          <Route index element={<Navigate to="owner-statements" replace />} />

          <Route path="summary" element={<LegacySummaryView />} />
          <Route
            index
            path="tax-statements/tax-settings"
            element={<Navigate to="accounts" replace />}
          />
          <Route
            path="tax-statements/tax-settings/:tabValue"
            element={<TaxStatementSettingsView />}
          />
          <Route
            path="templates"
            element={<Navigate to="/statements/templates/default" replace />}
          />
          <Route
            path="templates/:tabValue"
            element={<StatementLayoutsView />}
          />

          <Route path="settings/*" element={<LegacyTemplateView />} />

          <Route
            path=":tabValue"
            element={GL ? <StatementListView /> : <LegacyStatementListView />}
          />
        </Route>

        {GL ? (
          <>
            {/* Reconciliation */}
            <Route path="plaid-callback" element={<PlaidCallbackView />} />
            <Route path="reconciliation/*" element={<ReconciliationView />} />
            {/* Expenses */}
            <Route path="expenses" index element={<ExpensesOverviewView />} />
            {!isReadOnly && (
              <Route path="expenses/add" element={<ExpenseEditView />} />
            )}
            <Route
              path="expenses/:tabValue"
              element={<ExpensesOverviewView />}
            />
            <Route path="expense/:id" element={<ExpenseDetailView />} />
            <Route
              path="recurring/:id"
              element={<RecurringExpenseDetailView />}
            />
            <Route path="expense/:id/edit" element={<ExpenseEditView />} />
            {/* Deposits */}
            <Route path="deposits" index element={<DepositsOverviewView />} />
            {!isReadOnly && (
              <Route path="deposits/add" element={<DepositEditView />} />
            )}
            <Route
              path="deposits/:tabValue"
              element={<DepositsOverviewView />}
            />
            <Route path="deposit/:id" element={<DepositDetailView />} />
            <Route path="deposit/:id/edit" element={<DepositEditView />} />
            <Route
              path="deposit/:id/force-edit"
              element={<DepositEditView />}
            />
            <Route
              path="deposit/:id/:tabValue"
              element={<DepositDetailView />}
            />
            {/* Report */}
            <Route
              path="/general-ledger/*"
              element={<Navigate to="/reports" replace />}
            />

            <Route path="reports">
              <Route index element={<GeneralLedgerView />} />
              <Route path="*" element={<GeneralLedgerView />} />
            </Route>

            <Route path="fees-commission/:id/edit" element={<FeeEditView />} />
            <Route path="fees-commissions">
              <Route index element={<FeeRoutes />} />
              <Route path="*" element={<FeeRoutes />} />
            </Route>
          </>
        ) : (
          <>
            <Route path="custom-fees" element={<CustomFeesView />} />
            <Route path="custom-fees/:tabValue" element={<CustomFeesView />} />
            <Route
              path="custom-fees/default/:commission"
              element={<CustomFeesView />}
            />
            <Route path="reports">
              <Route index element={<ReportsView />} />
              <Route path=":id" element={<ReportsView />} />
            </Route>
          </>
        )}

        <Route path="settings/*" element={<SettingsView />} />

        {hasEnabledFeatures && (
          <Route path="features">
            <Route path=":tabValue" element={<FeaturesView />} />
            <Route index element={<Navigate to="features" replace />} />
          </Route>
        )}

        <Route
          path="callback"
          element={
            <Navigate
              to={`/connections${window.location.search || ''}&callback=true`}
            />
          }
        />

        {/* Redirect after login */}
        <Route
          path="login"
          element={<Navigate to={`/${window.location.search || ''}`} replace />}
        />
        <Route
          path="choose-role"
          element={<Navigate to={`/${window.location.search || ''}`} replace />}
        />

        {/* Redirect after onboarding */}
        <Route path="partner-agreement" element={<Navigate to="/" replace />} />

        <Route path="checkout-redirect" element={<Navigate to="/" replace />} />
        <Route
          path="connect-accounting"
          element={<Navigate to={`/${window.location.search || ''}`} />}
        />
        <Route path="select-listing" element={<Navigate to="/" replace />} />
        <Route
          path="start-date"
          element={<Navigate to={`/${window.location.search || ''}`} />}
        />
      </Routes>
    </Suspense>
  );
};
